/**
 * Scale360 Partner Funnel — shared pipeline stage order and referral funnel logic.
 *
 * Lives in shared/ (not server/ghl.ts) because both the rep dashboard and the
 * server need the same stage order. The client cannot import from server/, and
 * hand-mirroring the list is exactly how PIPELINE_STAGES in server/ghl.ts drifted
 * three stages out of date.
 */

/**
 * Scale360 Partner Funnel stage names, in pipeline order.
 * Pipeline ID mzgOuQhHmkDuixcpiSbj — confirmed against the live GHL API 2026-08-04.
 *
 * Order is load-bearing: the referral funnel derives progress from a stage's INDEX
 * in this array. Only ever append/rename to match GHL — never reorder to taste.
 *
 * NOTE: only 4 of these names have a matching leads.stage ENUM key (see
 * runMigration_legacy360TrustRecordedStage in server/db.ts). The three Analysis*
 * stages have no internal stage key at all, so ghlStageName is the ONLY place a
 * lead's analysis progress is observable.
 */
export const SCALE360_STAGE_ORDER = [
  "Lead Captured",                 // 1
  "Audit Submitted",               // 2
  "Analysis Report Requested",     // 3
  "Analysis Refinement Complete",  // 4
  "Analysis Submitted",            // 5
  "Follow-Up Active",              // 6
  "Payment Received",              // 7
  "Onboarding Submitted",          // 8
  "Active Client",                 // 9
  "Lost / No Response",            // 10 — terminal, deliberately outside the ladder
] as const;

/**
 * 1-based position of the first stage that proves the lead went through the
 * comprehensive analysis. In this funnel a lead reaches Follow-Up Active BY being
 * presented the analysis (audit -> analysis -> present -> follow up -> close), so
 * anything at or past this position has been through it. No upper bound: capping
 * this at the analysis stages is what left leads further down the funnel showing
 * an un-ticked Analysis badge despite having completed it.
 */
const STAGE_ANALYSIS = 3;
/** 1-based position of "Follow-Up Active" — the nurture milestone. */
const STAGE_NURTURE = 6;
/** 1-based position of "Payment Received" — the purchase milestone. */
const STAGE_PURCHASED = 7;

/** Terminal stage — a lost lead is not "further along" than an active one. */
const STAGE_LOST = "Lost / No Response";

function normalizeStageName(name: string): string {
  return name.trim().toLowerCase().replace(/[\s_-]+/g, " ");
}

/**
 * 1-based position of a live GHL stage name in the funnel, or 0 when unknown.
 *
 * Tolerant of the casing/spacing drift GHL stage names pick up over time
 * ("Follow Up Active" vs "Follow-Up Active"). Returns 0 for the terminal
 * Lost stage so it never counts as forward progress.
 */
export function scale360StageIndex(ghlStageName?: string | null): number {
  if (!ghlStageName) return 0;
  const target = normalizeStageName(ghlStageName);
  if (target === normalizeStageName(STAGE_LOST)) return 0;
  const i = SCALE360_STAGE_ORDER.findIndex(s => normalizeStageName(s) === target);
  return i === -1 ? 0 : i + 1;
}

/**
 * The internal leads.stage ENUM key mapped onto the same 1-based ladder.
 *
 * This is not merely a fallback — for payment it is the STRONGER signal. Scale360's
 * payment_received key is written by the Stripe webhook when money actually arrives
 * (server/billingRoutes.ts), whereas ghlStageName "Payment Received" only means
 * someone moved the card in Wibiz. Neither subsumes the other, so the funnel takes
 * whichever ladder is further along.
 */
const INTERNAL_STAGE_POSITION: Record<string, number> = {
  lead_captured: 1,
  audit_submitted: 2,
  follow_up_active: 6,
  payment_received: 7,
  onboarding_submitted: 8,
  active_client: 9,
  // lost_cold deliberately absent -> 0, same as STAGE_LOST above.
};

/** 1-based position from the internal stage key, or 0 when absent/unknown. */
export function scale360InternalStageIndex(stage?: string | null): number {
  if (!stage) return 0;
  return INTERNAL_STAGE_POSITION[stage] ?? 0;
}

/**
 * How far along the funnel a lead is, on the 1-based SCALE360_STAGE_ORDER ladder.
 * Takes the furthest of the live pipeline stage and the internal stage key so the
 * value is monotonic: it cannot regress when one source lags the other.
 */
export function scale360Progress(lead: Scale360FunnelLead): number {
  return Math.max(
    scale360StageIndex(lead.ghlStageName),
    scale360InternalStageIndex(lead.stage),
  );
}

/**
 * The subset of a lead this funnel reads. Declared structurally so both the
 * client's LeadRow and the server's DB row satisfy it without either importing
 * the other's type.
 */
export type Scale360FunnelLead = {
  stage?: string | null;
  ghlStageName?: string | null;
  // Revenue Audit signals (scorecard funnel)
  s360AuditScore?: string | null;
  s360AuditStatus?: string | null;
  s360PdfLink?: string | null;
  // Comprehensive Analysis signals (analysis funnel)
  s360AnalysisPdfUrl?: string | null;
  s360AnalysisSessionId?: string | null;
  s360AnalysisReportFileName?: string | null;
  s360AnalysisPlanName?: string | null;
};

/**
 * Milestones recorded server-side by the landing-page webhooks (repMilestones
 * table — see recordRepMilestone). Not yet wired into the rep dashboard; the
 * funnel accepts them so that signal can be added without reshaping callers.
 */
export type Scale360Milestones = {
  revenueAudit?: boolean;
  comprehensiveAnalysis?: boolean;
};

export type FunnelStep = { key: string; label: string; done: boolean };

const present = (v?: string | null) => typeof v === "string" && v.trim() !== "";

/**
 * Scale360 referral funnel status for the rep-facing Leads table.
 *
 * Progress is CUMULATIVE — a step stays ticked once earned, so a lead who reaches
 * Payment Received still shows In Nurture. Each step is satisfied by whichever
 * signal arrives first, because the lead's custom-field data and its pipeline
 * stage arrive by independent paths and either one can lag.
 *
 * Revenue Audit is deliberately NOT derived from stage position. The funnel has
 * two entry points: the scorecard audit and the comprehensive analysis form.
 * Leads entering via analysis skip the audit entirely (confirmed in production
 * 2026-08-04: a lead sitting at "Analysis Refinement Complete" with no audit
 * fields whatsoever), so an ordinal rule would tick a step they never completed.
 * There is no equivalent evidence of leads reaching stage 3+ without the
 * analysis, which is why that step DOES trust stage position.
 *
 * This function is pure and read-only. It never writes leads.stage and has no
 * bearing on commission eligibility (see isCommissionTriggerStage in server/db.ts).
 */
export function computeScale360Funnel(
  lead: Scale360FunnelLead,
  milestones: Scale360Milestones = {},
): FunnelStep[] {
  const idx = scale360Progress(lead);

  const hasAuditData =
    present(lead.s360AuditScore) ||
    present(lead.s360AuditStatus) ||
    present(lead.s360PdfLink);

  const hasAnalysisData =
    present(lead.s360AnalysisPdfUrl) ||
    present(lead.s360AnalysisSessionId) ||
    present(lead.s360AnalysisReportFileName) ||
    present(lead.s360AnalysisPlanName);

  return [
    { key: "engaged",   label: "Engaged",                done: true },
    { key: "audit",     label: "Revenue Audit",          done: hasAuditData || !!milestones.revenueAudit },
    { key: "analysis",  label: "Comprehensive Analysis", done: hasAnalysisData || !!milestones.comprehensiveAnalysis || idx >= STAGE_ANALYSIS },
    { key: "nurture",   label: "In Nurture",             done: idx >= STAGE_NURTURE },
    { key: "purchased", label: "Purchased",              done: idx >= STAGE_PURCHASED },
  ];
}
